import { produce } from 'immer'
import { SecondaryEmailResponse } from 'src/models/user.models'
import {
  SET_IS_ADD_SECONDARY_EMAIL_POPUP_OPEN,
  SetIsAddSecondaryEmailPopupOpenAction,
} from 'src/redux/actions/app.actions'
import {
  ADD_SECONDARY_EMAIL_FAILURE,
  ADD_SECONDARY_EMAIL_INIT,
  ADD_SECONDARY_EMAIL_SUCCESS,
  AddSecondaryEmailActions,
  GET_SECONDARY_EMAILS_FAILURE,
  GET_SECONDARY_EMAILS_INIT,
  GET_SECONDARY_EMAILS_SUCCESS,
  GetSecondaryEmailsActions,
  RESEND_VERIFICATION_CODE_FAILURE,
  RESEND_VERIFICATION_CODE_INIT,
  ResendVerificationCodeActions,
  VERIFY_SECONDARY_EMAIL_FAILURE,
  VERIFY_SECONDARY_EMAIL_INIT,
  VERIFY_SECONDARY_EMAIL_SUCCESS,
  VerifySecondaryEmailActions,
} from 'src/redux/actions/data/users.actions'
import { parseBackendErrors } from 'src/utils/error.utils'

export interface SecondaryEmailState {
  fetchingSecondaryEmails: boolean
  selectedSecondaryEmail?: string
  secondaryEmails: SecondaryEmailResponse[]
  secondaryEmailLoading: boolean
  secondaryEmailTopLevelError?: string
  resendVerificationCodeLoading: boolean
  resendVerificationCodeTopLevelError?: string
  verifySecondaryEmailLoading: boolean
  verifySecondaryEmailTopLevelError?: string
}

export const initialState: SecondaryEmailState = {
  fetchingSecondaryEmails: false,
  secondaryEmails: [],
  secondaryEmailLoading: false,
  resendVerificationCodeLoading: false,
  verifySecondaryEmailLoading: false,
}

export type Actions =
  | SetIsAddSecondaryEmailPopupOpenAction
  | AddSecondaryEmailActions
  | ResendVerificationCodeActions
  | VerifySecondaryEmailActions
  | GetSecondaryEmailsActions

// SecondaryEmailReducer is responsible for managing the state of AddSecondaryEmailPopup
const SecondaryEmailReducer = (
  state: SecondaryEmailState = initialState,
  action: Actions,
): SecondaryEmailState => {
  return produce(state, (draft) => {
    switch (action.type) {
      case GET_SECONDARY_EMAILS_INIT: {
        draft.fetchingSecondaryEmails = true
        return draft
      }

      case GET_SECONDARY_EMAILS_SUCCESS: {
        draft.fetchingSecondaryEmails = false
        draft.secondaryEmails = action.payload.secondaryEmails
        return draft
      }

      case GET_SECONDARY_EMAILS_FAILURE: {
        draft.fetchingSecondaryEmails = false
        return draft
      }

      case SET_IS_ADD_SECONDARY_EMAIL_POPUP_OPEN: {
        draft.selectedSecondaryEmail = action.payload
          .isAddSecondaryEmailPopupOpen
          ? action.payload.email
          : undefined
        draft.secondaryEmailTopLevelError = action.payload
          .isAddSecondaryEmailPopupOpen
          ? undefined
          : draft.secondaryEmailTopLevelError
        return draft
      }

      case ADD_SECONDARY_EMAIL_INIT: {
        draft.secondaryEmailTopLevelError = undefined
        return draft
      }

      case ADD_SECONDARY_EMAIL_SUCCESS: {
        draft.selectedSecondaryEmail = action.payload.secondaryEmail
        return draft
      }

      case ADD_SECONDARY_EMAIL_FAILURE: {
        const errors = parseBackendErrors(action.payload)

        draft.secondaryEmailTopLevelError = errors.topLevelError
        return draft
      }

      case RESEND_VERIFICATION_CODE_INIT: {
        draft.resendVerificationCodeLoading = true
        draft.resendVerificationCodeTopLevelError = undefined
        return draft
      }

      case RESEND_VERIFICATION_CODE_FAILURE: {
        const errors = parseBackendErrors(action.payload)

        draft.resendVerificationCodeLoading = false
        draft.resendVerificationCodeTopLevelError = errors.topLevelError
        return draft
      }

      case VERIFY_SECONDARY_EMAIL_INIT: {
        draft.verifySecondaryEmailLoading = true
        draft.verifySecondaryEmailTopLevelError = undefined
        return draft
      }

      case VERIFY_SECONDARY_EMAIL_SUCCESS: {
        draft.verifySecondaryEmailLoading = false
        return draft
      }

      case VERIFY_SECONDARY_EMAIL_FAILURE: {
        const errors = parseBackendErrors(action.payload)

        draft.verifySecondaryEmailLoading = false
        draft.verifySecondaryEmailTopLevelError = errors.topLevelError
        return draft
      }
    }
  })
}

export default SecondaryEmailReducer
